import React, { useState } from 'react'
import { Grid, CircularProgress } from '@mui/material'
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator'
import { useStyle } from '../../../../AccessBank/PoliciesProcessing/Components/MakerChecks/Style'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import ModalInc from '../../../../../shared-ui/ModalInc'
import AlertDialog from '../../../../../components/AlertDialog/index.es6'
import { hasAuthourity } from '../../../../../lib/access.es6'
import { reloadPage } from '../../../../../lib/utils.es6'
import SupportingDocumentUpload from '../../../../AccessBank/PoliciesProcessing/Components/MakerChecks/SupportingDocumentUpload'
import {
  SUPPORTING_DOCUMENT_LABELS,
  screeningFlagPrefix,
  screeningLabel,
  screeningReasons,
  findSupportingDocument
} from '../../../../AccessBank/PoliciesProcessing/lib/flaggedPolicyDecision'

// What the AML, PEP or IPRS screening actually found, shown both beside the
// stage and inside its decision modals. Without it the reviewer is asked to
// approve "PEP Approval" with the finding buried in the policy details tab.
const ScreeningReasons = props => {
  if (!props.reasons.length) {
    return null
  }
  // AML and PEP only ever screen the policy holder, so naming the subject
  // there would be noise. IPRS screens each person, and then it is the point.
  const named = props.reasons.length > 1
  return (
    <div className={props.className}>
      <span className={props.classes.screeningTitle}>
        {props.label} screening
      </span>
      {props.reasons.map(entry => (
        <div key={entry.subject} className={props.classes.screeningReason}>
          {named ? (
            <span className={props.classes.screeningSubject}>
              {entry.subject}:{' '}
            </span>
          ) : null}
          {entry.reason}
        </div>
      ))}
    </div>
  )
}

// One note-capture modal behind every stage action. They differ only in
// wording, in whether the note is mandatory, and in what submitting does.
// Screening stages (AML/PEP) additionally take a supporting document, and
// cannot be submitted without one.
const NoteModal = props => (
  <ModalInc
    modalTitle={props.title}
    subTitle={props.subTitle}
    open={props.open}
    close={props.onClose}
    fullWidth
  >
    <ValidatorForm onSubmit={() => props.onSubmit()}>
      <Grid container spacing={2}>
        {props.screeningReasons ? (
          <Grid item xs={12} sm={12} md={12}>
            {props.screeningReasons}
          </Grid>
        ) : null}
        {props.hideReason ? null : (
          <Grid item xs={12} sm={12} md={12}>
            <TextValidator
              name={props.name}
              id={props.name}
              label={props.label}
              value={props.value}
              onChange={event => props.onChange(event.target.value)}
              multiline
              rows={5}
              maxRows={4}
              fullWidth
              variant="outlined"
              validators={props.required ? ['required'] : []}
              errorMessages={props.required ? [props.requiredMessage] : []}
            />
          </Grid>
        )}
        {props.documentUpload ? (
          <Grid item xs={12} sm={12} md={12}>
            {props.documentUpload}
          </Grid>
        ) : null}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}></Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8} md={8}>
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                style={{ marginTop: '20px', float: 'right' }}
              >
                {props.submitText}
              </ButtonInc>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                style={{ marginTop: '20px' }}
                onClick={props.onClose}
              >
                Go back
              </ButtonInc>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ValidatorForm>
  </ModalInc>
)

// Fork of AccessBank/PoliciesProcessing/Components/MakerChecks, adding the
// Escalate action and the Senior Management decision.
//
// Multi-level (maker-checker) approval control. Shows the name of the single
// lowest-sequence Pending stage with inline Approve/Reject actions. The server
// owns separation of duties, distinct-actor-per-stage and sequencing; any
// violation comes back as a 4xx and is surfaced here. A stage rejection moves
// the policy to Rejected, after which the existing Re-Confirm / Discard branch
// takes over.
//
// Partners that opt in (allowRequestChanges) get a third option between those
// two: send the policy back for changes. That decides nothing — the stage
// keeps its Pending check — it only flags the policy need_action, which moves
// it out of this reviewer's queue and into the Need Action tab. While it is
// flagged the decision buttons are replaced by "Send Back for Approval", so
// the policy is unambiguously the other side's to act on; returning it clears
// the flag and puts it in the Corrected policies tab of the stage that asked.
const MakerChecks = props => {
  const classes = useStyle()

  const [openApproveDialog, setOpenApproveDialog] = useState(false)
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [openChangesDialog, setOpenChangesDialog] = useState(false)
  const [openSendBackDialog, setOpenSendBackDialog] = useState(false)
  const [openEscalateDialog, setOpenEscalateDialog] = useState(false)
  const [openAmlChangesDialog, setOpenAmlChangesDialog] = useState(false)
  const [openAmlSendBackDialog, setOpenAmlSendBackDialog] = useState(false)
  const [reason, setReason] = useState('')
  const [changesNotes, setChangesNotes] = useState('')
  const [sendBackNotes, setSendBackNotes] = useState('')
  const [escalateNotes, setEscalateNotes] = useState('')
  const [amlChangesNotes, setAmlChangesNotes] = useState('')
  const [amlSendBackNotes, setAmlSendBackNotes] = useState('')
  const [uploadedFile, setUploadedFile] = useState(null)
  const [openReturnDialog, setOpenReturnDialog] = useState(false)
  const [returnNotes, setReturnNotes] = useState('')

  const checks = (props.makerChecks || [])
    .slice()
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))

  if (!checks.length) {
    return ''
  }

  // The only actionable stage is the lowest-sequence check still Pending.
  let currentCheck = null
  for (let i = 0; i < checks.length; i++) {
    if (checks[i].status === 'Pending') {
      currentCheck = checks[i]
      break
    }
  }

  if (!currentCheck) {
    return ''
  }

  // On partners in DASHBOARD_ONLY_DECISION_PARTNERS (see PolicyHeader.js), the
  // whole maker-checks stage is withheld when the operator didn't arrive from
  // the review queue — props.dashboardGatePasses is already resolved there
  // and just forwarded (defaulting to allowed when the parent didn't set it).
  const passesDashboardGate =
    !props.requireDashboardOrigin || !!props.dashboardGatePasses
  if (!passesDashboardGate) {
    return ''
  }

  const canDecide = hasAuthourity('APPROVE_POLICY')

  // The prior approved stage — the one this stage would return to.
  // Used to name the button and the modal so the reviewer knows where it goes.
  const priorApproved = checks
    .filter(c => String(c.status || '').toLowerCase() === 'approved' && (c.sequence || 0) < (currentCheck.sequence || 0))
    .sort((a, b) => (b.sequence || 0) - (a.sequence || 0))[0]
  const priorStageLabel = priorApproved ? (priorApproved.label || priorApproved.type || 'Prior Stage').replace(/\s*Approval\b/gi, '').trim() : null
  const minSequence = Math.min(...checks.map(c => c.sequence || 0))

  const withManagement = !!props.escalatedToManagement
  const canDecideAsManagement = withManagement && !!props.canDecideEscalation

  // An AML or PEP stage is the compliance decision on a flagged policy, so it
  // is held to a stricter bar than the other stages: a reason on rejection, and
  // supporting evidence on offer for either decision.
  //
  // Approval never demands a reason from the reviewer — the API still records
  // one, so submitApprove sends a fixed "DONE" rather than asking the
  // reviewer to type something the decision itself already states. Neither
  // decision demands a document: the commonest AML rejection is "the
  // documents we need are missing", and an approval's evidence does not always
  // exist as a file — so requiring one blocked the decision rather than
  // recording it.
  const flagPrefix = screeningFlagPrefix(currentCheck)
  const supportingDocumentLabel = flagPrefix
    ? SUPPORTING_DOCUMENT_LABELS[flagPrefix]
    : null
  const existingDocument = flagPrefix
    ? findSupportingDocument(props.policy, flagPrefix)
    : null
  // Only offer the upload on stages that can actually take one — without a
  // policy guid or an upload handler there would be nowhere to put it. A
  // manager deciding an escalation did not run the screening, so they are not
  // asked for its evidence at all.
  const requiresEvidence =
    !!flagPrefix &&
    !!props.policyGuid &&
    !!props.uploadDocument &&
    !canDecideAsManagement

  const stageScreeningLabel = screeningLabel(currentCheck)
  const stageReasons = screeningReasons(currentCheck, props.policy)

  const renderScreeningReasons = () => (
    <ScreeningReasons
      label={stageScreeningLabel}
      reasons={stageReasons}
      classes={classes}
      className={classes.screeningPanel}
    />
  )

  const renderDocumentUpload = () =>
    requiresEvidence ? (
      <SupportingDocumentUpload
        policyGuid={props.policyGuid}
        label={supportingDocumentLabel}
        uploadDocument={props.uploadDocument}
        existingDocument={existingDocument}
        uploadedFile={uploadedFile}
        required={false}
        satisfied
        onUploaded={setUploadedFile}
      />
    ) : null

  // Sending a policy back for changes belongs to the level below, whose users
  // capture policies. Approvers are allowed it too: on a partner where the same
  // account both captures and approves at the lower level, gating on
  // CAPTURE_POLICY alone would strand a flagged policy with nobody able to
  // return it.
  const canSendBack =
    hasAuthourity('CAPTURE_POLICY') || hasAuthourity('APPROVE_POLICY')

  // A returned policy is out of this stage's hands until it comes back, so the
  // stage decisions are withheld while the flag is set.
  const returnedForChanges = !!props.needAction

  const amlNeedAction =
    !!(
      props.policy &&
      props.policy.additional_details &&
      props.policy.additional_details.aml_need_action
    )

  const canRequestChanges =
    !!props.allowRequestChanges &&
    canDecide &&
    !returnedForChanges &&
    !withManagement
  const canEscalate =
    !!props.allowEscalate && canDecide && !returnedForChanges && !withManagement
  const canReturn =
    !!props.allowReturnToPriorStage &&
    canDecide &&
    !returnedForChanges &&
    (currentCheck.sequence || 0) > minSequence &&
    !!priorStageLabel
  const canAmlRequestChanges =
    !!props.allowAmlRequestChanges &&
    canDecide &&
    !returnedForChanges &&
    !amlNeedAction &&
    !withManagement

  const submitRequestChanges = () => {
    setOpenChangesDialog(false)
    props.requestChanges(changesNotes)
    setChangesNotes('')
  }

  const submitEscalate = () => {
    setOpenEscalateDialog(false)
    props.escalate(escalateNotes)
    setEscalateNotes('')
  }

  const submitAmlRequestChanges = () => {
    setOpenAmlChangesDialog(false)
    props.amlRequestChanges(amlChangesNotes)
    setAmlChangesNotes('')
  }

  const submitAmlSendBack = () => {
    setOpenAmlSendBackDialog(false)
    props.amlSendBackForApproval(amlSendBackNotes)
    setAmlSendBackNotes('')
  }

  const submitSendBack = () => {
    setOpenSendBackDialog(false)
    props.sendBackForApproval(sendBackNotes)
    setSendBackNotes('')
  }

  const submitReturn = () => {
    if (!returnNotes.trim()) return
    setOpenReturnDialog(false)
    props.returnToPriorStage({
      check_guid: currentCheck.guid,
      reason: returnNotes.trim()
    })
    setReturnNotes('')
  }

  const submitApprove = () => {
    setOpenApproveDialog(false)
    props.decideMakerCheck({
      decision: 'Approved',
      check_guid: currentCheck.guid,
      reason: 'DONE'
    })
  }

  const submitReject = () => {
    if (!reason.trim()) return
    setOpenRejectDialog(false)
    props.decideMakerCheck({
      decision: 'Rejected',
      reason,
      check_guid: currentCheck.guid
    })
  }

  // Show the human stage label ("AML Check", "Quality Check Approval") rather
  // than the raw approval type ("AgentApproval") the server names in its
  // separation-of-duties / sequencing errors.
  const humanizeStage = message => {
    if (!message || !currentCheck.type || !currentCheck.label) return message
    return message.split(currentCheck.type).join(currentCheck.label)
  }

  const readError = error => {
    if (!error) return ''
    let message
    if (typeof error === 'string') message = error
    else if (Array.isArray(error.message)) message = error.message.join(', ')
    else if (error.message) message = error.message
    else return 'An unexpected error occurred'
    return humanizeStage(message)
  }

  const successMessage = () => {
    const result = props.decisionResult || {}
    if (result.decision === 'Rejected') {
      return `Policy ${props.policyNumber} was rejected at this stage and moved to Rejected.`
    }
    if (result.activated) {
      return `All approval stages complete. Policy ${props.policyNumber} is now activated.`
    }
    return `Stage approved. Policy ${props.policyNumber} now awaits the next approval stage.`
  }

  // The handoff is not a stage decision, so it needs its own copy. Naming the
  // direction matters: "sent back" and "returned for approval" are opposite
  // moves and the checker needs to know which one just happened.
  const HANDOFF_COPY = {
    requestChanges: {
      progress: `Sending policy ${props.policyNumber} back for changes...`,
      success: `Policy ${props.policyNumber} was sent back for changes and now sits in the Need Action queue.`,
      error: `Could not send policy ${props.policyNumber} back for changes`
    },
    escalate: {
      progress: `Escalating policy ${props.policyNumber} to Senior Management...`,
      success: `Policy ${props.policyNumber} was escalated and now sits in the Senior Management queue.`,
      error: `Could not escalate policy ${props.policyNumber} to Senior Management`
    },
    sendToReview: {
      progress: `Returning policy ${props.policyNumber} for approval...`,
      success: `Policy ${props.policyNumber} was returned and now awaits approval at the same stage.`,
      error: `Could not return policy ${props.policyNumber} for approval`
    },
    amlRequestChanges: {
      progress: `Flagging policy ${props.policyNumber} for AML action...`,
      success: `Policy ${props.policyNumber} has been flagged and now sits in the AML Need Action queue.`,
      error: `Could not flag policy ${props.policyNumber} for AML action`
    },
    amlSendBack: {
      progress: `Returning policy ${props.policyNumber} for AML approval...`,
      success: `Policy ${props.policyNumber} was returned and now awaits AML review.`,
      error: `Could not return policy ${props.policyNumber} for AML approval`
    }
  }

  const handoffCopy =
    HANDOFF_COPY[props.needActionDirection] || HANDOFF_COPY.sendToReview

  const needActionProgress = handoffCopy.progress
  const needActionSuccessMessage = handoffCopy.success
  const needActionErrorTitle = handoffCopy.error

  return (
    <div className={classes.wrapper}>
      {returnedForChanges ? (
        canSendBack ? (
          <div className={classes.actions}>
            <ButtonInc
              style={{ background: '#008000' }}
              onClick={() => setOpenSendBackDialog(true)}
            >
              Send Back for Approval
            </ButtonInc>
          </div>
        ) : (
          <span className={classes.noPerm}>
            This policy was sent back for changes. See the policy notes for what
            was asked for.
          </span>
        )
      ) : amlNeedAction ? (
        canSendBack ? (
          <div className={classes.actions}>
            <ButtonInc
              style={{ background: '#008000' }}
              onClick={() => setOpenAmlSendBackDialog(true)}
            >
              Send Back for Approval
            </ButtonInc>
          </div>
        ) : (
          <span className={classes.noPerm}>
            This policy has been flagged for AML action and is pending
            resolution.
          </span>
        )
      ) : withManagement ? (
        canDecideAsManagement ? (
          <div className={classes.actions}>
            <ButtonInc onClick={() => setOpenApproveDialog(true)}>
              Approve
            </ButtonInc>
            <ButtonInc
              style={{ background: '#8F191C' }}
              onClick={() => setOpenRejectDialog(true)}
            >
              Reject
            </ButtonInc>
            {canReturn ? (
              <ButtonInc
                style={{ background: '#7B3F00' }}
                onClick={() => setOpenReturnDialog(true)}
              >
                Return to {priorStageLabel}
              </ButtonInc>
            ) : null}
          </div>
        ) : (
          <span className={classes.noPerm}>
            This policy has been escalated to Senior Management and is awaiting
            their review.
          </span>
        )
      ) : canDecide ? (
        <div className={classes.actions}>
          <ButtonInc onClick={() => setOpenApproveDialog(true)}>
            Approve
          </ButtonInc>
          {canEscalate ? (
            <ButtonInc
              style={{ background: '#B9770E' }}
              onClick={() => setOpenEscalateDialog(true)}
            >
              Escalate
            </ButtonInc>
          ) : null}
          {canReturn ? (
            <ButtonInc
              style={{ background: '#7B3F00' }}
              onClick={() => setOpenReturnDialog(true)}
            >
              Return to {priorStageLabel}
            </ButtonInc>
          ) : null}
          {canAmlRequestChanges ? (
            <ButtonInc
              style={{ background: '#1976d2' }}
              onClick={() => setOpenAmlChangesDialog(true)}
            >
              Request Changes
            </ButtonInc>
          ) : null}
          {canRequestChanges ? (
            <ButtonInc
              style={{ background: '#1976d2' }}
              onClick={() => setOpenChangesDialog(true)}
            >
              Request Changes
            </ButtonInc>
          ) : null}
          <ButtonInc
            style={{ background: '#8F191C' }}
            onClick={() => setOpenRejectDialog(true)}
          >
            Reject
          </ButtonInc>
        </div>
      ) : (
        <span className={classes.noPerm}>
          You do not have permission to decide this stage.
        </span>
      )}

      <ScreeningReasons
        label={stageScreeningLabel}
        reasons={stageReasons}
        classes={classes}
        className={`${classes.screeningPanel} ${classes.screeningPanelInline}`}
      />

      <NoteModal
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
        title={
          canDecideAsManagement
            ? `Approve escalated policy ${props.policyNumber}?`
            : `Approve this stage for policy ${props.policyNumber}?`
        }
        subTitle={
          canDecideAsManagement
            ? 'The policy goes straight to New Business, bypassing the AML Desk.'
            : requiresEvidence
            ? 'You may attach a supporting document.'
            : null
        }
        hideReason
        onSubmit={submitApprove}
        submitText="Approve Stage"
        documentUpload={renderDocumentUpload()}
        screeningReasons={renderScreeningReasons()}
      />

      <NoteModal
        open={openChangesDialog}
        onClose={() => setOpenChangesDialog(false)}
        title="Send this policy back for changes"
        subTitle={
          'The policy stays at this stage but moves to the Need Action queue ' +
          'until the changes are made and it is sent back.'
        }
        name="changesNotes"
        label="Enter the changes required"
        value={changesNotes}
        onChange={setChangesNotes}
        onSubmit={submitRequestChanges}
        submitText="Request Changes"
        required
        requiredMessage="Please describe the changes required"
      />

      <NoteModal
        open={openEscalateDialog}
        onClose={() => setOpenEscalateDialog(false)}
        title={`Escalate policy ${props.policyNumber} to Senior Management`}
        subTitle={
          'The policy stays at this stage and moves to the Senior Management ' +
          'queue. They approve it straight through to New Business, or reject ' +
          'it outright.'
        }
        name="escalateNotes"
        label="Enter the reason for escalating"
        value={escalateNotes}
        onChange={setEscalateNotes}
        onSubmit={submitEscalate}
        submitText="Escalate to Management"
        required
        requiredMessage="Please enter a reason for escalating this policy"
        screeningReasons={renderScreeningReasons()}
      />

      <NoteModal
        open={openAmlChangesDialog}
        onClose={() => setOpenAmlChangesDialog(false)}
        title="Flag this AML policy for action"
        subTitle={
          'The policy stays at this stage and moves to the AML Need Action ' +
          'queue until the issue is resolved.'
        }
        name="amlChangesNotes"
        label="Enter the reason for requesting changes"
        value={amlChangesNotes}
        onChange={setAmlChangesNotes}
        onSubmit={submitAmlRequestChanges}
        submitText="Request Changes"
        required
        requiredMessage="Please describe the changes required"
      />

      <NoteModal
        open={openAmlSendBackDialog}
        onClose={() => setOpenAmlSendBackDialog(false)}
        title={`Send policy ${props.policyNumber} back for AML approval`}
        subTitle="The AML need action flag is cleared and the policy returns to the AML review queue."
        name="amlSendBackNotes"
        label="Note on what was resolved (optional)"
        value={amlSendBackNotes}
        onChange={setAmlSendBackNotes}
        onSubmit={submitAmlSendBack}
        submitText="Send Back for Approval"
      />

      <NoteModal
        open={openSendBackDialog}
        onClose={() => setOpenSendBackDialog(false)}
        title={`Send policy ${props.policyNumber} back for approval`}
        subTitle="It returns to the approval queue it was sent back from."
        name="sendBackNotes"
        label="Note on what was changed (optional)"
        value={sendBackNotes}
        onChange={setSendBackNotes}
        onSubmit={submitSendBack}
        submitText="Send Back for Approval"
      />

      <NoteModal
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        title={`Return this policy to ${priorStageLabel}`}
        subTitle={`The current stage will be marked as returned. A new pending check will be created for the ${priorStageLabel} team to re-review.`}
        name="returnNotes"
        label={`Reason for returning to ${priorStageLabel}`}
        value={returnNotes}
        onChange={setReturnNotes}
        onSubmit={submitReturn}
        submitText={`Return to ${priorStageLabel}`}
        required
        requiredMessage={`Please enter a reason for returning this policy to ${priorStageLabel}`}
      />

      <AlertDialog
        custom
        show={props.loader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Recording decision for policy {props.policyNumber}...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.success}
        size="sm"
        title={successMessage()}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="Cancel"
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.error}
        danger
        title={`Could not record the decision for policy ${props.policyNumber}`}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.error)}
      </AlertDialog>

      {/* The handoff gets its own progress, success and failure surface. It used
          to ride on the generic "Policy updated successfully" dialog, which
          said nothing about which way the policy went — and showed nothing at
          all when the write failed, leaving the flag silently unchanged. */}
      <AlertDialog
        custom
        show={!!props.needActionLoader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>{needActionProgress}</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={!!props.needActionSuccess}
        size="sm"
        title={needActionSuccessMessage}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        cancelBtnText="Cancel"
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.needActionError}
        danger
        title={needActionErrorTitle}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.needActionError)}
      </AlertDialog>

      <AlertDialog
        custom
        show={!!props.returnToPriorStageLoader}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Returning policy {props.policyNumber} to {priorStageLabel}...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={!!props.returnToPriorStageSuccess}
        size="sm"
        title={`Policy ${props.policyNumber} returned to ${priorStageLabel}.`}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        showCancel={false}
        showConfirm={false}
        confirmBtnCssClass
      >
        <div>
          <ButtonInc
            variant="contained"
            onClick={() => reloadPage()}
            style={{ background: 'green' }}
          >
            Ok
          </ButtonInc>
        </div>
      </AlertDialog>

      <AlertDialog
        show={!!props.returnToPriorStageError}
        danger
        title={`Could not return policy ${props.policyNumber}`}
        onConfirm={() => reloadPage()}
        confirmBtnText="Ok"
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        {readError(props.returnToPriorStageError)}
      </AlertDialog>

      <NoteModal
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
        title={
          canDecideAsManagement
            ? `Reject escalated policy ${props.policyNumber}`
            : 'Reject this approval stage'
        }
        subTitle={
          canDecideAsManagement
            ? "The policy is rejected outright and returned to the AML Desk's Rejected tab. Nobody can action it afterwards."
            : requiresEvidence
            ? 'A reason is required. You may attach a supporting document.'
            : 'A reason is required when rejecting a stage.'
        }
        name="reason"
        label="Enter reason for rejecting this stage"
        value={reason}
        onChange={setReason}
        onSubmit={submitReject}
        submitText="Reject Stage"
        required
        requiredMessage="Please enter a reason for rejecting this stage"
        documentUpload={renderDocumentUpload()}
        screeningReasons={renderScreeningReasons()}
      />
    </div>
  )
}

export default MakerChecks
